/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Credits Management DTOs & Schemas (Task 5.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * System-wide credit KPI metrics schema
 */
export const AdminCreditsOverviewSchema = z
  .object({
    totalCirculatingCredits: z
      .number()
      .openapi({ description: 'Total credits currently in circulation across all workspace wallets' }),
    totalCreditsUsedThisMonth: z
      .number()
      .openapi({ description: 'Total credits consumed in the current calendar month' }),
    totalLifetimeAllocated: z
      .number()
      .openapi({ description: 'Total lifetime credits granted or allocated since inception' }),
    totalWalletsCount: z
      .number()
      .openapi({ description: 'Total number of registered workspace wallets' }),
    totalAdjustmentsCount: z
      .number()
      .openapi({ description: 'Number of manual admin adjustments recorded' }),
    activeWorkspacesCount: z
      .number()
      .openapi({ description: 'Workspaces active with credit transactions this month' }),
  })
  .openapi('AdminCreditsOverview');

export type AdminCreditsOverview = z.infer<typeof AdminCreditsOverviewSchema>;

/**
 * Single workspace wallet item schema for Admin directory
 */
export const AdminWorkspaceWalletItemSchema = z
  .object({
    workspaceId: z.string().openapi({ description: 'Workspace ID' }),
    workspaceName: z.string().openapi({ description: 'Workspace display name' }),
    slug: z.string().openapi({ description: 'Workspace URL slug' }),
    ownerEmail: z.string().openapi({ description: 'Owner user email' }),
    ownerName: z.string().openapi({ description: 'Owner user full name' }),
    balance: z.number().openapi({ description: 'Current active credit balance' }),
    reservedBalance: z.number().openapi({ description: 'Credits currently reserved for queued jobs' }),
    lifetimeCredits: z.number().openapi({ description: 'Cumulative lifetime credits granted' }),
    updatedAt: z.number().openapi({ description: 'Unix epoch timestamp of last update' }),
    status: z.enum(['healthy', 'warning', 'critical']).openapi({ description: 'Wallet health status' }),
    totalTransactions: z.number().openapi({ description: 'Total number of transactions on wallet' }),
  })
  .openapi('AdminWorkspaceWalletItem');

export type AdminWorkspaceWalletItem = z.infer<typeof AdminWorkspaceWalletItemSchema>;

/**
 * Single immutable credit ledger item schema for Admin audit log
 */
export const AdminLedgerItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Immutable transaction ID' }),
    workspaceId: z.string().openapi({ description: 'Workspace ID' }),
    workspaceName: z.string().openapi({ description: 'Workspace name' }),
    userEmail: z.string().optional().openapi({ description: 'Executing user email if available' }),
    amount: z.number().openapi({ description: 'Credit change: positive (+) or deduction (-)' }),
    balanceAfter: z.number().openapi({ description: 'Resulting balance after transaction' }),
    reason: z.string().openapi({ description: 'Reason code or descriptive rationale' }),
    referenceId: z.string().nullable().optional().openapi({ description: 'Reference ID e.g. job ID, order ID' }),
    modelCode: z.string().nullable().optional().openapi({ description: 'AI Model code used e.g. gpt-4o' }),
    tokensUsed: z.number().nullable().optional().openapi({ description: 'Tokens or units consumed' }),
    createdAt: z.string().openapi({ description: 'ISO 8601 creation timestamp' }),
  })
  .openapi('AdminLedgerItem');

export type AdminLedgerItem = z.infer<typeof AdminLedgerItemSchema>;

